/**
 * Shared fetch infrastructure for AI Operations API
 */
import { AI_OPERATIONS_API_URL } from '../config';
import { resilientFetch, ApiError } from '../resilientFetch';
import { demoModeManager } from '../demoModeManager';
import { getAIOpsMockData } from '../mockData';

/**
 * Validate UUID format (RFC 4122 compliant)
 */
export const isValidUUID = (uuid: string | null | undefined): boolean => {
  if (!uuid || typeof uuid !== 'string') return false;

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

/**
 * Get the selected client ID from localStorage
 * Used by partner/aws users who need to specify which client they're viewing
 * @returns {string|null} The selected client ID or null
 */
const getSelectedClientId = (): string | null => {
  return localStorage.getItem('selectedClientId');
};

/**
 * Append client_id to URL if available (for partner/aws users)
 * @param {string} endpoint - The API endpoint
 * @returns {string} The endpoint with client_id appended if available
 */
const getSelectedProjectId = (): string | null => {
  return localStorage.getItem('selectedProjectId');
};

const appendClientId = (endpoint: string): string => {
  const clientId = getSelectedClientId();
  if (!clientId) return endpoint;

  // Check if endpoint already has query parameters
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}client_id=${clientId}`;
};

const appendProjectId = (endpoint: string): string => {
  // Use global filter if available
  try {
    const stored = localStorage.getItem('globalFilter');
    if (stored) {
      const { filterType, selectedId } = JSON.parse(stored);
      if (selectedId) {
        const separator = endpoint.includes('?') ? '&' : '?';
        if (filterType === 'project') {
          return `${endpoint}${separator}project_id=${selectedId}`;
        } else if (filterType === 'workload') {
          return `${endpoint}${separator}workload_id=${selectedId}`;
        } else if (filterType === 'account') {
          return `${endpoint}${separator}account_id=${selectedId}`;
        } else if (filterType === 'environment') {
          return `${endpoint}${separator}environment_id=${selectedId}`;
        }
      }
    }
  } catch {
    // ignore
  }

  // Backward compat
  const projectId = getSelectedProjectId();
  if (!projectId) return endpoint;

  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}project_id=${projectId}`;
};

/**
 * Generic fetch wrapper with error handling and automatic retry
 * Includes JWT authentication for multi-tenant isolation
 * Automatically includes client_id for partner/aws users
 * Uses resilientFetch for automatic retries with exponential backoff
 */
export const fetchAPI = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  // Check if demo mode is active
  const { isDemoMode } = demoModeManager.getDemoMode();
  if (isDemoMode) {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
    const endpointWithClientId = appendClientId(endpoint);
    const endpointWithProjectId = appendProjectId(endpointWithClientId);
    return getAIOpsMockData(endpointWithProjectId);
  }

  try {
    // Append client_id and project_id to endpoint
    const endpointWithClientId = appendClientId(endpoint);
    const endpointWithProjectId = appendProjectId(endpointWithClientId);
    const url = `${AI_OPERATIONS_API_URL}${endpointWithProjectId}`;
    const token = localStorage.getItem('token');

    // Destructure headers from options to merge properly
    const { headers: optionHeaders, ...restOptions } = options;

    // Use resilientFetch for automatic retry with exponential backoff
    const { data } = await resilientFetch(
      url,
      {
        ...restOptions,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...optionHeaders,
        },
      },
      'genaiops' // Service identifier for health tracking
    );

    return data;
  } catch (error) {
    console.error(`AI-Operations API Error [${endpoint}]:`, error);

    // Re-throw with a cleaner message for UI
    if (error instanceof ApiError) {
      throw new Error(error.message);
    }
    throw error;
  }
};
